<script setup lang="ts">
import { toRefs, onMounted, ref, computed } from "vue";
import type { Stripe, StripeElements as SE } from "@stripe/stripe-js";
import type { Ref } from "vue";
import { StripeElements, StripeElement } from "vue-stripe-js";
import { usePaymentStore } from "@/stores/payment";
import { storeToRefs } from "pinia";
import { createStripeSession } from "@/graphql/functions/createStripeSession";
import type { StripeSessionModel } from "@/models/checkout/StripeSessionModel";
import type { DeliveryMethods } from "@/models/basket/DeliveryMethods";
import { useRouter } from "vue-router";
import { handleError } from "@/services/AlertService";

const props = withDefaults(
    defineProps<{
        bookingId: string;
        fulfilmentMethod?: string;
    }>(),
    {
        bookingId: "",
        fulfilmentMethod: undefined,
    }
);
const router = useRouter();
const { bookingId, fulfilmentMethod } = toRefs(props);
const secret: Ref<string | null> = ref(null);
const customerSecretSession: Ref<string | null> = ref(null);
const store = usePaymentStore();
const elementsOptions = computed(() => ({
    clientSecret: secret.value,
    customerSessionClientSecret: customerSecretSession.value,
}));
const instanceOptions = computed(() => ({
    stripeAccount: store.stripeAccountId,
}));
const paymentElement: Ref<typeof StripeElement | null> = ref(null);
const parentElement: Ref<typeof StripeElements | null> = ref(null);

// Once we're mounted, we'll call Rocket to get the client secret
onMounted(async () => {
    // Create input object with all available parameters
    const sessionInput: StripeSessionModel = {
        bookingId: bookingId.value,
    };

    // Only add optional parameters if they exist
    if (fulfilmentMethod.value) {
        // Convert the string to enum value
        // Assuming fulfilmentMethod.value is "TOD" or another valid enum value
        sessionInput.fulfilmentMethod = fulfilmentMethod.value as DeliveryMethods;
    }

    if (store.guestUserEmail) sessionInput.guestCustomerEmail = store.guestUserEmail;
    if (store.guestUserFirstName) sessionInput.guestCustomerFirstName = store.guestUserFirstName;
    if (store.guestUserLastName) sessionInput.guestCustomerLastName = store.guestUserLastName;

    let clientSecretResponse = await createStripeSession(sessionInput);
    clientSecretResponse = clientSecretResponse.stripePaymentSession;
    if (clientSecretResponse.customerSecretSession) {
        customerSecretSession.value = clientSecretResponse.customerSecretSession;
    }
    secret.value = clientSecretResponse.sessionId;
});

const { stripePublishableKey } = storeToRefs(store);
const isLoadingStripe: Ref<boolean> = ref(true);

const ready = () => {
    isLoadingStripe.value = false;
};

// Calls function in the payment store to handle the payment submission to Stripe and then onwards.
function handleSubmitClick() {
    try {
        // @ts-ignore-next-line
        const stripeInstance = parentElement.value.instance as Stripe;
        const currentRoute = router.resolve("/");
        const absoluteURL = new URL(currentRoute.href, window.location.origin).href;

        // Check if the stripeInstance is available
        if (!stripeInstance) {
            console.error("Stripe instance is not available");
            return;
        }

        const stripeObject = {
            stripeInstance,
            confirmPaymentObject: {
                // @ts-ignore-next-line
                elements: parentElement.value.elements as SE,
                confirmParams: {
                    return_url: absoluteURL,
                },
                redirect: "if_required",
            },
        };
        store.onSubmitPaymentButtonClick(fulfilmentMethod.value as DeliveryMethods, bookingId.value, stripeObject);
    } catch (error) {
        console.error("Error in handleSubmitClick:", error);
        handleError({ detail: "Unknown error occured - please refresh and try again." });
    }
}
</script>

<template>
    <div>
        <StripeElements v-if="store.stripeReady && secret && instanceOptions.stripeAccount != null" :class="isLoadingStripe ? 'loading-stripe' : ''" v-slot="{ elements }" ref="parentElement" :stripe-key="stripePublishableKey" :elements-options="elementsOptions" :instance-options="instanceOptions">
            <StripeElement @ready="ready" ref="paymentElement" type="payment" :elements="elements"></StripeElement>
        </StripeElements>
        <StripeElements v-else-if="store.stripeReady && secret && instanceOptions.stripeAccount == null" :class="isLoadingStripe ? 'loading-stripe' : ''" v-slot="{ elements }" ref="parentElement" :stripe-key="stripePublishableKey" :elements-options="elementsOptions">
            <StripeElement @ready="ready" ref="paymentElement" type="payment" :elements="elements"></StripeElement>
        </StripeElements>
        <div v-else class="d-flex justify-center">
            <v-progress-circular :indeterminate="true" :size="50" :width="4" color="#666"> </v-progress-circular>
        </div>
        <div>
            <v-expand-transition>
                <!-- vuetify button to pay -->
                <v-btn class="mt-4 primary-button" :disabled="store.isFinalPaymentStepLoading" @click="handleSubmitClick()" v-show="!isLoadingStripe">
                    <font-awesome-icon icon="fa-lock fa-solid" class="mr-2"></font-awesome-icon>
                    Pay Securely <v-progress-circular indeterminate size="15" class="ml-1" v-if="store.isFinalPaymentStepLoading"></v-progress-circular>
                </v-btn>
            </v-expand-transition>
        </div>
        <div v-if="store.stripePaymentErrorToDisplay">
            <v-alert border="start" density="compact" variant="flat" color="var(--vt-c-warning)" class="mt-4">
                <span v-if="store.stripePaymentErrorToDisplay.message">{{ store.stripePaymentErrorToDisplay.message }}</span>
                <span v-else
                    >Something went wrong, code: <samp>{{ store.stripePaymentErrorToDisplay.code }}</samp></span
                >
            </v-alert>
        </div>
    </div>
</template>

<style scoped lang="scss">
.loading-stripe {
    min-height: 147px;
}
</style>
